import { SpwkUtilV3 } from "../dao/SpwkUtilV3";
import { SpwkMetaData, SpwkMetaData_DataType } from "./SpwkMetaData";
import { SpwkMetaTable } from "./SpwkMetaTable";

/**
 * RunNormalization 可能有很多步驟，統一集中在此檔
 */
export class ExportNormalizor {
  /** 合理化答案 - (變為如果不合併，受檢者應該會怎樣填寫答案)
   * 至此，手上有各特作的中繼表
   * 表裡面簡單先填入了 rhf_value
   *
   * 接下來要處理一些事情:
   * - 處理Trigger: 對每個選項，比對所有特作中繼表(不只自己)，如果相關選項勾了，則自己也要勾起來
   * - 處理同階層其他: 較為複雜，再想想...
   * - 處理最終的其他: 較為複雜，再想想...
   * - 處理無: 最後看同一層的選項是否都沒有勾選 (打勾 或 取消打勾 都要判斷)
   * - 處理以上皆無: 看所有題目中的 無，判斷是否勾選  (打勾 或 取消打勾 都要判斷)
   */
  static RunNormalization(meta_table_list: SpwkMetaTable[]) {
    // 噪音既往病史 & 自覺症狀為獨立頁面
    // 與其他特作應無連動項目

    // 既往病史所有選項
    const allMedicalHistory = meta_table_list.map(table => table.meta_rows.filter(row => row.chapter === SpwkUtilV3.CHAPTER_既往病史 && (row.data_type === SpwkMetaData_DataType.Option || row.data_type === SpwkMetaData_DataType.Input)))
      .reduce((a, b) => a.concat(b), []);
    this.ProcessTriggers(allMedicalHistory);
    this.ProcessOthers(allMedicalHistory);
    this.ProcessNoneOptions(allMedicalHistory);

    // 自覺症狀所有選項
    const allSymptoms = meta_table_list.map(table => table.meta_rows.filter(row => row.chapter === SpwkUtilV3.CHAPTER_自覺症狀 && (row.data_type === SpwkMetaData_DataType.Option || row.data_type === SpwkMetaData_DataType.Input)))
      .reduce((a, b) => a.concat(b), []);
    this.ProcessTriggers(allSymptoms);
    this.ProcessOthers(allSymptoms);
    this.ProcessNoneOptions(allSymptoms);

    // 處理拆分項目，將其子項的答案回填至主項目上
    meta_table_list.forEach(table => {
      const splitOptions = table.meta_rows.filter(row => row.is_split_parent);
      if (!splitOptions || splitOptions.length === 0) {
        return;
      }

      splitOptions.forEach(parent => {
        // 取得拆分出的子項列表
        const children = table.meta_rows.filter(row => row.split_from_guid === parent.option_guid);

        // 處理勾選項目
        if (/_無$/gi.test(parent.code) || /^以上皆無$/gi.test(parent.code)) {
          // 若父項為 無 或 以上皆無，則必須全子項都勾選才算
          parent.rhf_value = children.every(child => child.rhf_value);
        }
        else {
          // 否則只要子項一個有勾就算
          parent.rhf_value = children.some(child => child.rhf_value);
        }

        // 處理輸入項
        const parentInputs = table.meta_rows.filter(row => row.data_type === SpwkMetaData_DataType.Input && row.option_guid === parent.option_guid);
        parentInputs.forEach(input => {
          // 取得子項的輸入內容
          const childInputs = table.meta_rows.filter(row =>
            row.data_type === SpwkMetaData_DataType.Input &&
            row.code === input.code &&
            children.some(child => child.option_guid === row.option_guid) &&
            row.rhf_value).map(row => row.rhf_value);

          // 合併子項的輸入內容
          input.rhf_value = childInputs.join(",");
        });
      });
    });
  }

  /** 
   * Triggers 處理 
   * @param rows - SpwkMetaData Rows
   * */
  private static ProcessTriggers(rows: SpwkMetaData[]) {
    rows = rows.filter(r => r.data_type === SpwkMetaData_DataType.Option);

    // 整理所有選項的答案對照表
    const mappings = rows.reduce((m, row) => {
      if (!m.has(row.code)) {
        m.set(row.code, row.rhf_value);
      }
      return m;
    }, new Map<string, boolean>())

    // 整理所有 Option 的 Triggers
    const triggers = rows.reduce((m, row) => {
      if (row.triggers && row.triggers.length > 0) {
        const re = (m.get(row.code) || []).concat(row.triggers.split("﹋"));
        m.set(row.code, Array.from(new Set(re)));
      }
      return m;
    }, new Map<string, string[]>())

    // 對所有 Triggers 觸發答案連動
    rows.forEach(row => {
      if (row.rhf_value) {
        this.SetAnswerRecursive(mappings, triggers, row.code, []);
      }
    });

    // 將最終答案回填
    rows.forEach(row => {
      row.rhf_value = mappings.get(row.code);
    });
  }

  /** 遞迴觸發勾選連動 
   * @param answers - 所有答案對照表
   * @param trigger - 所有Option Code 的 Triggers 對照表
   * @param current - 當前處理中的Option Code
   * @param processing - 在此遞迴中已處理的Option Code
  */
  private static SetAnswerRecursive(answers: Map<string, boolean>, triggers: Map<string, string[]>, current: string, processing: string[]) {
    // 如 current 已處理過則不再進行處理，避免進入死循環。
    if (processing.indexOf(current) > -1) {
      return;
    }
    processing.push(current);
    if (answers.has(current)) {
      answers.set(current, true);

      if (triggers.has(current)) {
        triggers.get(current)?.forEach(trigger => this.SetAnswerRecursive(answers, triggers, trigger, processing));
      }
    }
  }

  /**
   * 處理 無、以上皆無 選項
   * @param rows - SpwkMetaData Rows
   */
  private static ProcessNoneOptions(rows: SpwkMetaData[]) {
    // 依特作代碼 & 題目分群
    const questionGroups = rows.reduce((m, row) => {
      const key = `${row.func_code},${row.question_guid}`;
      const items = m.get(key) || [];
      items.push(row);
      m.set(key, items);
      return m;
    }, new Map<string, SpwkMetaData[]>());

    // 每小題分別處理 無 的選項
    questionGroups.forEach((g, key) => {
      const noneOptions = g.filter(r => /^.*_無$/g.test(r.code));
      if (noneOptions != null) {
        const result = g.filter(r => !noneOptions.some(x => x.id === r.id)).every(r => r.rhf_value !== true);
        for (let option of noneOptions) {
          option.rhf_value = result;
        }
      }
    });

    const workGroups = rows.reduce((m, row) => {
      const key = row.func_code;
      const items = m.get(key) || [];
      items.push(row);
      m.set(key, items);
      return m;
    }, new Map<string, SpwkMetaData[]>());

    // 每個作業分別處理 以上皆無 的選項
    workGroups.forEach(g => {
      // 以上皆無 處理
      const allNoneOption = g.find(r => /^以上皆無$/g.test(r.code));
      if (allNoneOption) {
        const result = g.filter(r => !/(^.*_無$|^以上皆無$)/g.test(r.code) && r.data_type === SpwkMetaData_DataType.Option).every(r => r.rhf_value !== true);
        allNoneOption.rhf_value = result;
      }
    });
  }

  /**
   * 處理 其他 項目應填入的內容
   * @param rows - SpwkMetaData Rows
   */
  private static ProcessOthers(rows: SpwkMetaData[]) {
    // 先整理好完整回答的資料，忽略所有 _無、以上皆無 的項目勾選，最後再處理
    const answers = rows.reduce((m, row) => {
      const key = row.data_type === SpwkMetaData_DataType.Input ? row.rhf_name : row.code;
      if (!m.has(key) && row.rhf_value && !/(^.*_無$|^以上皆無$)/g.test(key)) {
        m.set(key, row.rhf_value);
      }
      return m;
    }, new Map<string, boolean | string>());

    // 將rows依作業代碼分群
    const groups = rows.reduce((m, row) => {
      const items = m.get(row.func_code) || [];
      items.push(row);
      m.set(row.func_code, items);
      return m;
    }, new Map<string, SpwkMetaData[]>());

    groups.forEach((g, code) => {
      // 產生一份答案處理核對表
      const answerProcessStatus = Array.from(answers.keys())
        .reduce((m, key) => {
          // 本身作業的答案視為已處理
          const processed = g.some(r => r.data_type === SpwkMetaData_DataType.Input ? r.rhf_name === key : r.code === key);
          m.set(key, processed)
          return m;
        }, new Map<string, boolean>());

      // #region 處理各小題的 其他 欄位

      // 取得非 其他_其他 的 其他 欄位
      const otherOptions = g.filter(r => /(?!其他)^.*_其他$/g.test(r.code));
      otherOptions.forEach(option => {
        // 取出前端顯示同群組中所有勾選的答案代碼
        const v3GroupAnswers = Array.from(new Set(rows.filter(r => r.func_code !== code && r.v3_group === option.v3_group).map(r => {
          const key = r.data_type === SpwkMetaData_DataType.Input ? r.rhf_name : r.code;
          return key;
        }).filter(x => x && answers.has(x) && !answerProcessStatus.get(x))));

        // 取得當前欄位的輸入項
        const input = rows.find(r => r.option_guid === option.option_guid && r.data_type === SpwkMetaData_DataType.Input);
        if (v3GroupAnswers?.length > 0 && input) {
          const inputAnswer = answers.get(input.rhf_name) || "";

          // 取得各選項應填入的內容
          const result = v3GroupAnswers.map(answerKey => {
            const [val, keys] = this.GetInputAnswer(rows, answerKey, answers);
            // 處理 Answer Process Status
            keys.forEach(key => answerProcessStatus.set(key, true));
            return val;
          }).join(",");

          // 將同群組但不存於此表單答案整合至輸入欄
          input.rhf_value = inputAnswer ? `${inputAnswer},${result}` : result;
          // 將選項勾選，避免原選項無勾選但有輸入內容的問題
          option.rhf_value = true;
        }
      });

      // #endregion

      // 未處理的答案列表
      const unprocessedAnswers = Array.from(answers.keys()).filter(key => !answerProcessStatus.get(key));
      // 沒有尚未處理的答案則完成此作業的 其他欄位 填入
      if (!unprocessedAnswers || unprocessedAnswers.length === 0) {
        return;
      }

      // #region 處理 `其他_其他` 應填入的結果
      const endOption = g.find(r => /^其他_其他$/g.test(r.code));
      if (!endOption) {
        return;
        // throw new Error(`作業代碼: ${code}, 尚有以下答案未填入但未找到 其他_其他 欄位可填, [${unprocessedAnswers.join(",")}]`);
      }

      const endInput = g.find(r => r.option_guid === endOption.option_guid && r.data_type === SpwkMetaData_DataType.Input);
      if (!endInput) {
        return;
        // throw new Error(`作業代碼: ${code}, 尚有以下答案未填入但未找到 其他_其他 欄位可填, [${unprocessedAnswers.join(",")}]`);
      }

      const endAnswer = answers.get(endInput.rhf_name) || "";
      // 取得各選項應填入的內容
      const result = unprocessedAnswers.map(answerKey => {
        const [val, keys] = this.GetInputAnswer(rows, answerKey, answers);
        // 處理 Answer Process Status
        keys.forEach(key => answerProcessStatus.set(key, true));
        return val;
      }).filter(x => x).join(",");

      // 將同群組但不存於此表單答案整合至輸入欄
      endInput.rhf_value = endAnswer ? `${endAnswer},${result}` : result;
      // 將選項勾選，避免原選項無勾選但有輸入內容的問題
      endOption.rhf_value = true;

      // #endregion
    });
  }

  /**
   * 取得特定 Code 的輸入內容
   * @param rows - SpwkMetaData Rows
   * @param code - 要取得輸入內容的 Code
   * @param answers - 完整的答案表
   * @returns - Code及其輸入的內容，例如 心臟血管(貧血,...), 處理過的答案Key值
   */
  private static GetInputAnswer(rows: SpwkMetaData[], code: string, answers: Map<string, boolean | string>): [string, string[]] {
    if (/^.*●input_\d*$/gi.test(code)) {
      // 理論上input輸入皆掛於某option下，故input不需單獨輸出答案
      return ["", [code]];
    }
    else {
      const pattern = new RegExp(`^(.*)\\.${code}●input_\\d*$`, "gi");
      const keys = Array.from(rows.filter(r => r.data_type === SpwkMetaData_DataType.Input && pattern.test(r.rhf_name) && answers.has(r.rhf_name))
        .reduce((s, r) => s.add(r.rhf_name), new Set<string>()));

      if (keys?.length === 0) {
        return [code, [code]];
      }

      const val = `${keys.map(key => answers.get(key)).join(",")}`;

      if (/^.*其他_其他$/gi.test(code)) {
        return [val, keys.concat(code)];
      }

      const m = code.match(/^(?<code>.+)_其他$/);
      return [m ? `${m.groups?.code}(${val})` : `${code}(${val})`, keys.concat(code)];
    }
  }
}
